import React, { useState, useEffect } from 'react';
import { Key, Save, Eye, EyeOff, AlertCircle, CheckCircle2, Copy, Check, Send, Bell } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { FacebookSetupGuide } from './FacebookSetupGuide';

interface ApiCredentials {
  facebook_app_id: string;
  facebook_app_secret: string;
  instagram_app_id: string;
  instagram_app_secret: string;
  instagram_direct_app_id: string;
  instagram_direct_app_secret: string;
  instagram_direct_oauth_url: string;
  linkedin_client_id: string;
  linkedin_client_secret: string;
  linkedin_social_client_id: string;
  linkedin_social_client_secret: string;
  youtube_client_id: string;
  youtube_client_secret: string;
  google_business_client_id: string;
  google_business_client_secret: string;
  tiktok_client_key: string;
  tiktok_client_secret: string;
  tiktok_business_client_id: string;
  tiktok_business_client_secret: string;
  x_api_key: string;
  x_api_secret: string;
  x_bearer_token: string;
  pinterest_app_id: string;
  pinterest_app_secret: string;
  /** 'trial' rozmawia z api-sandbox.pinterest.com, 'standard' z api.pinterest.com. */
  pinterest_access_tier: 'trial' | 'standard';
  // Telegram settings
  telegram_bot_token: string;
  telegram_chat_id: string;
  telegram_notifications_enabled: boolean;
}

export function ApiSettings() {
  const [credentials, setCredentials] = useState<ApiCredentials>({
    facebook_app_id: '',
    facebook_app_secret: '',
    instagram_app_id: '',
    instagram_app_secret: '',
    instagram_direct_app_id: '',
    instagram_direct_app_secret: '',
    instagram_direct_oauth_url: '',
    linkedin_client_id: '',
    linkedin_client_secret: '',
    linkedin_social_client_id: '',
    linkedin_social_client_secret: '',
    youtube_client_id: '',
    youtube_client_secret: '',
    google_business_client_id: '',
    google_business_client_secret: '',
    tiktok_client_key: '',
    tiktok_client_secret: '',
    tiktok_business_client_id: '',
    tiktok_business_client_secret: '',
    x_api_key: '',
    x_api_secret: '',
    x_bearer_token: '',
    pinterest_app_id: '',
    pinterest_app_secret: '',
    pinterest_access_tier: 'trial',
    telegram_bot_token: '',
    telegram_chat_id: '',
    telegram_notifications_enabled: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState({
    facebook: false,
    instagram: false,
    instagramDirect: false,
    linkedin: false,
    linkedinSocial: false,
    youtube: false,
    googleBusiness: false,
    tiktok: false,
    tiktokBusiness: false,
    x: false,
    pinterest: false,
    telegram: false,
  });
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [testingTelegram, setTestingTelegram] = useState(false);
  const [hasCustomKeys, setHasCustomKeys] = useState(false);

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('user_settings')
        .select('facebook_app_id, facebook_app_secret, instagram_app_id, instagram_app_secret, instagram_direct_app_id, instagram_direct_app_secret, instagram_direct_oauth_url, linkedin_client_id, linkedin_client_secret, linkedin_social_client_id, linkedin_social_client_secret, youtube_client_id, youtube_client_secret, google_business_client_id, google_business_client_secret, tiktok_client_key, tiktok_client_secret, tiktok_business_client_id, tiktok_business_client_secret, x_api_key, x_api_secret, x_bearer_token, pinterest_app_id, pinterest_app_secret, pinterest_access_tier, telegram_bot_token, telegram_chat_id, telegram_notifications_enabled')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setCredentials({
          facebook_app_id: data.facebook_app_id || '',
          facebook_app_secret: data.facebook_app_secret || '',
          instagram_app_id: data.instagram_app_id || '',
          instagram_app_secret: data.instagram_app_secret || '',
          instagram_direct_app_id: data.instagram_direct_app_id || '',
          instagram_direct_app_secret: data.instagram_direct_app_secret || '',
          instagram_direct_oauth_url: data.instagram_direct_oauth_url || '',
          linkedin_client_id: data.linkedin_client_id || '',
          linkedin_client_secret: data.linkedin_client_secret || '',
          linkedin_social_client_id: data.linkedin_social_client_id || '',
          linkedin_social_client_secret: data.linkedin_social_client_secret || '',
          youtube_client_id: data.youtube_client_id || '',
          youtube_client_secret: data.youtube_client_secret || '',
          google_business_client_id: data.google_business_client_id || '',
          google_business_client_secret: data.google_business_client_secret || '',
          tiktok_client_key: data.tiktok_client_key || '',
          tiktok_client_secret: data.tiktok_client_secret || '',
          tiktok_business_client_id: data.tiktok_business_client_id || '',
          tiktok_business_client_secret: data.tiktok_business_client_secret || '',
          x_api_key: data.x_api_key || '',
          x_api_secret: data.x_api_secret || '',
          x_bearer_token: data.x_bearer_token || '',
          pinterest_app_id: data.pinterest_app_id || '',
          pinterest_app_secret: data.pinterest_app_secret || '',
          pinterest_access_tier: data.pinterest_access_tier === 'standard' ? 'standard' : 'trial',
          telegram_bot_token: data.telegram_bot_token || '',
          telegram_chat_id: data.telegram_chat_id || '',
          telegram_notifications_enabled: data.telegram_notifications_enabled || false,
        });
        setHasCustomKeys(
          !!(data.facebook_app_id || data.instagram_app_id || data.linkedin_client_id || data.youtube_client_id || data.google_business_client_id || data.tiktok_client_key)
        );
      }
    } catch (error) {
      console.error('Error loading API credentials:', error);
      toast.error('Failed to load API credentials');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: existingSettings } = await supabase
        .from('user_settings')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (existingSettings) {
        const { error } = await supabase
          .from('user_settings')
          .update({
            facebook_app_id: credentials.facebook_app_id || null,
            facebook_app_secret: credentials.facebook_app_secret || null,
            instagram_app_id: credentials.instagram_app_id || null,
            instagram_app_secret: credentials.instagram_app_secret || null,
            instagram_direct_app_id: credentials.instagram_direct_app_id || null,
            instagram_direct_app_secret: credentials.instagram_direct_app_secret || null,
            instagram_direct_oauth_url: credentials.instagram_direct_oauth_url || null,
            linkedin_client_id: credentials.linkedin_client_id || null,
            linkedin_client_secret: credentials.linkedin_client_secret || null,
            linkedin_social_client_id: credentials.linkedin_social_client_id || null,
            linkedin_social_client_secret: credentials.linkedin_social_client_secret || null,
            youtube_client_id: credentials.youtube_client_id || null,
            youtube_client_secret: credentials.youtube_client_secret || null,
            google_business_client_id: credentials.google_business_client_id || null,
            google_business_client_secret: credentials.google_business_client_secret || null,
            tiktok_client_key: credentials.tiktok_client_key || null,
            tiktok_client_secret: credentials.tiktok_client_secret || null,
            tiktok_business_client_id: credentials.tiktok_business_client_id || null,
            tiktok_business_client_secret: credentials.tiktok_business_client_secret || null,
            x_api_key: credentials.x_api_key || null,
            x_api_secret: credentials.x_api_secret || null,
            x_bearer_token: credentials.x_bearer_token || null,
            pinterest_app_id: credentials.pinterest_app_id || null,
            pinterest_app_secret: credentials.pinterest_app_secret || null,
            pinterest_access_tier: credentials.pinterest_access_tier,
            telegram_bot_token: credentials.telegram_bot_token || null,
            telegram_chat_id: credentials.telegram_chat_id || null,
            telegram_notifications_enabled: credentials.telegram_notifications_enabled,
            updated_at: new Date().toISOString(),
          })
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('user_settings')
          .insert({
            user_id: user.id,
            facebook_app_id: credentials.facebook_app_id || null,
            facebook_app_secret: credentials.facebook_app_secret || null,
            instagram_app_id: credentials.instagram_app_id || null,
            instagram_app_secret: credentials.instagram_app_secret || null,
            instagram_direct_app_id: credentials.instagram_direct_app_id || null,
            instagram_direct_app_secret: credentials.instagram_direct_app_secret || null,
            instagram_direct_oauth_url: credentials.instagram_direct_oauth_url || null,
            linkedin_client_id: credentials.linkedin_client_id || null,
            linkedin_client_secret: credentials.linkedin_client_secret || null,
            linkedin_social_client_id: credentials.linkedin_social_client_id || null,
            linkedin_social_client_secret: credentials.linkedin_social_client_secret || null,
            youtube_client_id: credentials.youtube_client_id || null,
            youtube_client_secret: credentials.youtube_client_secret || null,
            google_business_client_id: credentials.google_business_client_id || null,
            google_business_client_secret: credentials.google_business_client_secret || null,
            tiktok_client_key: credentials.tiktok_client_key || null,
            tiktok_client_secret: credentials.tiktok_client_secret || null,
            tiktok_business_client_id: credentials.tiktok_business_client_id || null,
            tiktok_business_client_secret: credentials.tiktok_business_client_secret || null,
            x_api_key: credentials.x_api_key || null,
            x_api_secret: credentials.x_api_secret || null,
            x_bearer_token: credentials.x_bearer_token || null,
            pinterest_app_id: credentials.pinterest_app_id || null,
            pinterest_app_secret: credentials.pinterest_app_secret || null,
            pinterest_access_tier: credentials.pinterest_access_tier,
            telegram_bot_token: credentials.telegram_bot_token || null,
            telegram_chat_id: credentials.telegram_chat_id || null,
            telegram_notifications_enabled: credentials.telegram_notifications_enabled,
          });

        if (error) throw error;
      }

      toast.success('API credentials saved successfully');
      setHasCustomKeys(
        !!(credentials.facebook_app_id || credentials.instagram_app_id || credentials.linkedin_client_id || credentials.youtube_client_id || credentials.google_business_client_id || credentials.tiktok_client_key)
      );
    } catch (error) {
      console.error('Error saving API credentials:', error);
      toast.error('Failed to save API credentials');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const CopyButton = ({ text, field }: { text: string; field: string }) => (
    <button
      onClick={() => copyToClipboard(text, field)}
      className="inline-flex items-center space-x-1 px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded border border-gray-300 transition-colors"
    >
      {copiedField === field ? (
        <><Check className="w-3 h-3 text-green-600" /><span className="text-green-600">Copied</span></>
      ) : (
        <><Copy className="w-3 h-3" /><span>Copy</span></>
      )}
    </button>
  );

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center space-x-3 mb-6">
        <Key className="w-6 h-6 text-blue-600" />
        <div>
          <h2 className="text-xl font-semibold">API Settings</h2>
          <p className="text-sm text-gray-500">Configure your own API credentials for social media integrations</p>
        </div>
      </div>

      {hasCustomKeys && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-green-900">Using Custom API Keys</h3>
              <p className="text-sm text-green-700 mt-1">
                You are currently using your own API credentials. This gives you better control and higher rate limits.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-2">Why provide your own API keys?</p>
            <ul className="list-disc list-inside space-y-1 text-blue-800">
              <li>Higher rate limits for your account</li>
              <li>Better privacy and data control</li>
              <li>Independent from shared rate limits</li>
              <li>Direct access to your Facebook/Instagram apps</li>
            </ul>
            <p className="mt-3 font-medium">How to get API credentials:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-800">
              <li>
                <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">
                  Create a Facebook App
                </a> for Facebook and Instagram (same app for both)
              </li>
              <li>
                <a href="https://www.linkedin.com/developers/apps" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">
                  Create a LinkedIn App
                </a> for LinkedIn
              </li>
              <li>
                <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-600">
                  Create a Google Cloud Project
                </a> for YouTube and Google Business Profile (separate OAuth clients)
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        <div className="border-b pb-6">
          <h3 className="text-lg font-medium mb-4">Facebook & Instagram API</h3>
          <p className="text-sm text-gray-600 mb-4">
            <strong>Important:</strong> Use the same Facebook App for both Facebook and Instagram. The credentials below should be identical to Instagram credentials.
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                App ID
              </label>
              <input
                type="text"
                value={credentials.facebook_app_id}
                onChange={(e) => setCredentials({ ...credentials, facebook_app_id: e.target.value })}
                placeholder="Enter your Facebook App ID"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                App Secret
              </label>
              <div className="relative">
                <input
                  type={showSecrets.facebook ? 'text' : 'password'}
                  value={credentials.facebook_app_secret}
                  onChange={(e) => setCredentials({ ...credentials, facebook_app_secret: e.target.value })}
                  placeholder="Enter your Facebook App Secret"
                  className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets({ ...showSecrets, facebook: !showSecrets.facebook })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecrets.facebook ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <FacebookSetupGuide />
          </div>
        </div>

        <div className="border-b pb-6">
          <h3 className="text-lg font-medium mb-4">Instagram API (via Facebook)</h3>
          <p className="text-sm text-gray-600 mb-4">
            <strong>Note:</strong> Instagram via Facebook uses the same Facebook App. The credentials should be identical to Facebook credentials above.
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                App ID
              </label>
              <input
                type="text"
                value={credentials.instagram_app_id}
                onChange={(e) => setCredentials({ ...credentials, instagram_app_id: e.target.value })}
                placeholder="Enter your Instagram App ID (same as Facebook)"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                App Secret
              </label>
              <div className="relative">
                <input
                  type={showSecrets.instagram ? 'text' : 'password'}
                  value={credentials.instagram_app_secret}
                  onChange={(e) => setCredentials({ ...credentials, instagram_app_secret: e.target.value })}
                  placeholder="Enter your Instagram App Secret (same as Facebook)"
                  className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets({ ...showSecrets, instagram: !showSecrets.instagram })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecrets.instagram ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800 mb-2 font-medium">
                Instagram Webhook Configuration (Both Types):
              </p>
              <p className="text-sm text-blue-800">
                Both Instagram connection types use the same webhook URL as Facebook: <code className="bg-white px-1 rounded">meta-webhook-dispatcher</code> (configured above). Make sure to subscribe to <strong>comments</strong> and <strong>messages</strong> fields in your Facebook App webhook settings.
              </p>
              <p className="text-sm text-blue-800 mt-2">
                <strong>Required permissions:</strong> instagram_basic, instagram_content_publish, instagram_manage_insights, pages_show_list, pages_read_engagement
              </p>
              <p className="text-sm text-blue-800 mt-2 font-medium">
                Features:
              </p>
              <ul className="text-sm text-blue-800 list-disc list-inside space-y-1 ml-2">
                <li>Automatic Unicode formatting in replies</li>
                <li>Post permalink and media data in webhooks</li>
                <li>Platform-specific logging with [instagram] prefix</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-b pb-6">
          <h3 className="text-lg font-medium mb-4">Instagram Direct API</h3>
          <p className="text-sm text-gray-600 mb-4">
            <strong>New:</strong> Separate Instagram App for Instagram Direct connections (no Facebook Page required).
          </p>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-purple-900 font-medium mb-2">
              Instagram Direct - Independent Connection:
            </p>
            <ul className="text-sm text-purple-800 list-disc list-inside space-y-1 ml-2">
              <li>Direct connection to Instagram Business/Creator (no Facebook Page needed)</li>
              <li>Use a different Instagram App than Facebook/Instagram (via Facebook)</li>
              <li>Ideal for users who want separate API limits and credentials</li>
            </ul>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instagram Direct App ID
              </label>
              <input
                type="text"
                value={credentials.instagram_direct_app_id}
                onChange={(e) => setCredentials({ ...credentials, instagram_direct_app_id: e.target.value })}
                placeholder="Enter your Instagram Direct App ID"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instagram Direct App Secret
              </label>
              <div className="relative">
                <input
                  type={showSecrets.instagramDirect ? 'text' : 'password'}
                  value={credentials.instagram_direct_app_secret}
                  onChange={(e) => setCredentials({ ...credentials, instagram_direct_app_secret: e.target.value })}
                  placeholder="Enter your Instagram Direct App Secret"
                  className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets({ ...showSecrets, instagramDirect: !showSecrets.instagramDirect })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecrets.instagramDirect ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Instagram Direct OAuth URL <span className="text-red-600">*</span>
              </label>
              <textarea
                value={credentials.instagram_direct_oauth_url}
                onChange={(e) => setCredentials({ ...credentials, instagram_direct_oauth_url: e.target.value })}
                placeholder="Paste your Instagram Business Login OAuth URL here"
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono text-xs"
              />
              <p className="text-xs text-gray-500 mt-1">
                Example: https://www.instagram.com/oauth/authorize?force_reauth=true&client_id=YOUR_ID&redirect_uri=...
              </p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <p className="text-sm text-purple-800 mb-2 font-medium">
                Setup Instructions:
              </p>
              <ol className="text-sm text-purple-800 list-decimal list-inside space-y-2 ml-2">
                <li>Create a <strong>NEW</strong> Facebook App at <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="underline">Facebook Developers</a></li>
                <li>Add <strong>Instagram Basic Display</strong> product</li>
                <li>Go to <strong>Instagram Business Login</strong> settings</li>
                <li>Copy the <strong>Embed URL</strong> from "Set up Instagram business login" section</li>
                <li>Paste the URL in the field above</li>
                <li>In <strong>Instagram Basic Display → Settings</strong>, add OAuth Redirect URI:</li>
              </ol>
              <div className="mt-2 bg-white border border-purple-300 rounded p-2 flex items-center gap-2">
                <code className="text-xs text-purple-900 break-all flex-1 min-w-0">
                  https://sms.aiagenthub.pl/auth/meta/callback
                </code>
                <CopyButton
                  text="https://sms.aiagenthub.pl/auth/meta/callback"
                  field="instagram-direct-oauth-callback"
                />
              </div>
              <p className="text-sm text-purple-800 mt-3">
                <strong>Required permissions:</strong> instagram_basic, instagram_content_publish, instagram_manage_comments, instagram_manage_messages
              </p>
              <p className="text-sm text-purple-800 mt-2 font-medium">
                Why separate credentials?
              </p>
              <ul className="text-sm text-purple-800 list-disc list-inside space-y-1 ml-2">
                <li>Independent API rate limits</li>
                <li>Separate app for different business units</li>
                <li>Better organization and tracking</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-b pb-6">
          <h3 className="text-lg font-medium mb-4">LinkedIn API - Posting</h3>
          <p className="text-sm text-gray-600 mb-4">For publishing posts to LinkedIn</p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client ID
              </label>
              <input
                type="text"
                value={credentials.linkedin_client_id}
                onChange={(e) => setCredentials({ ...credentials, linkedin_client_id: e.target.value })}
                placeholder="Enter your LinkedIn Client ID"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Secret
              </label>
              <div className="relative">
                <input
                  type={showSecrets.linkedin ? 'text' : 'password'}
                  value={credentials.linkedin_client_secret}
                  onChange={(e) => setCredentials({ ...credentials, linkedin_client_secret: e.target.value })}
                  placeholder="Enter your LinkedIn Client Secret"
                  className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets({ ...showSecrets, linkedin: !showSecrets.linkedin })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecrets.linkedin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800 mb-2 font-medium">
                OAuth Redirect URL:
              </p>
              <div className="bg-white border border-blue-300 rounded p-2 flex items-center gap-2">
                <code className="text-xs text-blue-900 break-all flex-1 min-w-0">
                  https://sms.aiagenthub.pl/linkedin-callback
                </code>
                <CopyButton
                  text="https://sms.aiagenthub.pl/linkedin-callback"
                  field="linkedin-callback"
                />
              </div>
              <p className="text-xs text-blue-700 mt-2">
                Add this URL to your LinkedIn App's "Auth" tab under "OAuth 2.0 settings" → "Redirect URLs"
              </p>
            </div>
          </div>
        </div>

        <div className="border-b pb-6">
          <h3 className="text-lg font-medium mb-4">LinkedIn Social API - Comments</h3>
          <p className="text-sm text-gray-600 mb-4">For managing comments and Social Inbox (requires Community Management API)</p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client ID
              </label>
              <input
                type="text"
                value={credentials.linkedin_social_client_id}
                onChange={(e) => setCredentials({ ...credentials, linkedin_social_client_id: e.target.value })}
                placeholder="Enter your LinkedIn Social Client ID"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Secret
              </label>
              <div className="relative">
                <input
                  type={showSecrets.linkedinSocial ? 'text' : 'password'}
                  value={credentials.linkedin_social_client_secret}
                  onChange={(e) => setCredentials({ ...credentials, linkedin_social_client_secret: e.target.value })}
                  placeholder="Enter your LinkedIn Social Client Secret"
                  className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets({ ...showSecrets, linkedinSocial: !showSecrets.linkedinSocial })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecrets.linkedinSocial ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800 mb-2 font-medium">
                OAuth Redirect URL:
              </p>
              <div className="bg-white border border-blue-300 rounded p-2 flex items-center gap-2">
                <code className="text-xs text-blue-900 break-all flex-1 min-w-0">
                  https://sms.aiagenthub.pl/linkedin-callback
                </code>
                <CopyButton
                  text="https://sms.aiagenthub.pl/linkedin-callback"
                  field="linkedin-social-callback"
                />
              </div>
              <p className="text-xs text-blue-700 mt-2">
                Add this URL to your LinkedIn App's "Auth" tab under "OAuth 2.0 settings" → "Redirect URLs"
              </p>
            </div>
          </div>
        </div>

        <div className="border-b pb-6">
          <h3 className="text-lg font-medium mb-4">YouTube API</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client ID
              </label>
              <input
                type="text"
                value={credentials.youtube_client_id}
                onChange={(e) => setCredentials({ ...credentials, youtube_client_id: e.target.value })}
                placeholder="Enter your YouTube OAuth Client ID"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Secret
              </label>
              <div className="relative">
                <input
                  type={showSecrets.youtube ? 'text' : 'password'}
                  value={credentials.youtube_client_secret}
                  onChange={(e) => setCredentials({ ...credentials, youtube_client_secret: e.target.value })}
                  placeholder="Enter your YouTube OAuth Client Secret"
                  className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets({ ...showSecrets, youtube: !showSecrets.youtube })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecrets.youtube ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800 mb-2 font-medium">
                Setup Instructions:
              </p>
              <ol className="text-sm text-blue-800 list-decimal list-inside space-y-2 ml-2">
                <li>Go to <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                <li>Create a new OAuth 2.0 Client ID (Web application)</li>
                <li>Enable <strong>YouTube Data API v3</strong></li>
                <li>Add this redirect URI:</li>
              </ol>
              <div className="mt-2 bg-white border border-blue-300 rounded p-2 flex items-center gap-2">
                <code className="text-xs text-blue-900 break-all flex-1 min-w-0">
                  https://xahssxqgkrrlotaunwtg.supabase.co/functions/v1/youtube-oauth
                </code>
                <CopyButton
                  text="https://xahssxqgkrrlotaunwtg.supabase.co/functions/v1/youtube-oauth"
                  field="youtube-redirect"
                />
              </div>
              <p className="text-sm text-blue-800 mt-3 font-medium">
                Required OAuth Scopes:
              </p>
              <div className="mt-2 space-y-2">
                <div className="bg-white border border-blue-300 rounded p-2 flex items-center gap-2">
                  <code className="text-xs text-blue-900 break-all flex-1 min-w-0">
                    https://www.googleapis.com/auth/youtube.force-ssl
                  </code>
                  <CopyButton
                    text="https://www.googleapis.com/auth/youtube.force-ssl"
                    field="youtube-scope-1"
                  />
                </div>
                <div className="bg-white border border-blue-300 rounded p-2 flex items-center gap-2">
                  <code className="text-xs text-blue-900 break-all flex-1 min-w-0">
                    https://www.googleapis.com/auth/youtube.readonly
                  </code>
                  <CopyButton
                    text="https://www.googleapis.com/auth/youtube.readonly"
                    field="youtube-scope-2"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-b pb-6">
          <h3 className="text-lg font-medium mb-4">Google Business Profile API</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client ID
              </label>
              <input
                type="text"
                value={credentials.google_business_client_id}
                onChange={(e) => setCredentials({ ...credentials, google_business_client_id: e.target.value })}
                placeholder="Enter your Google Business OAuth Client ID"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Secret
              </label>
              <div className="relative">
                <input
                  type={showSecrets.googleBusiness ? 'text' : 'password'}
                  value={credentials.google_business_client_secret}
                  onChange={(e) => setCredentials({ ...credentials, google_business_client_secret: e.target.value })}
                  placeholder="Enter your Google Business OAuth Client Secret"
                  className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets({ ...showSecrets, googleBusiness: !showSecrets.googleBusiness })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecrets.googleBusiness ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800 mb-2 font-medium">
                Setup Instructions:
              </p>
              <ol className="text-sm text-blue-800 list-decimal list-inside space-y-2 ml-2">
                <li>Go to <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" className="underline">Google Cloud Console</a></li>
                <li>Create a <strong>separate</strong> OAuth 2.0 Client ID (Web application)</li>
                <li>Enable <strong>Google My Business API</strong></li>
                <li>Add this redirect URI:</li>
              </ol>
              <div className="mt-2 bg-white border border-blue-300 rounded p-2 flex items-center gap-2">
                <code className="text-xs text-blue-900 break-all flex-1 min-w-0">
                  https://xahssxqgkrrlotaunwtg.supabase.co/functions/v1/google-business-oauth
                </code>
                <CopyButton
                  text="https://xahssxqgkrrlotaunwtg.supabase.co/functions/v1/google-business-oauth"
                  field="google-business-redirect"
                />
              </div>
              <p className="text-sm text-blue-800 mt-3 font-medium">
                Required OAuth Scope:
              </p>
              <div className="mt-2">
                <div className="bg-white border border-blue-300 rounded p-2 flex items-center gap-2">
                  <code className="text-xs text-blue-900 break-all flex-1 min-w-0">
                    https://www.googleapis.com/auth/business.manage
                  </code>
                  <CopyButton
                    text="https://www.googleapis.com/auth/business.manage"
                    field="google-business-scope"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="pb-6">
          <h3 className="text-lg font-medium mb-4">TikTok API</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Key
              </label>
              <input
                type="text"
                value={credentials.tiktok_client_key}
                onChange={(e) => setCredentials({ ...credentials, tiktok_client_key: e.target.value })}
                placeholder="Enter your TikTok Client Key"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Secret
              </label>
              <div className="relative">
                <input
                  type={showSecrets.tiktok ? 'text' : 'password'}
                  value={credentials.tiktok_client_secret}
                  onChange={(e) => setCredentials({ ...credentials, tiktok_client_secret: e.target.value })}
                  placeholder="Enter your TikTok Client Secret"
                  className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets({ ...showSecrets, tiktok: !showSecrets.tiktok })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecrets.tiktok ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800 mb-2 font-medium">
                Setup Instructions:
              </p>
              <ol className="text-sm text-blue-800 list-decimal list-inside space-y-2 ml-2">
                <li>Go to <a href="https://developers.tiktok.com/apps/" target="_blank" rel="noopener noreferrer" className="underline">TikTok for Developers</a></li>
                <li>Create a new app or select existing app</li>
                <li>Add <strong>Login Kit</strong> and <strong>Video Kit</strong> products</li>
                <li>Add this redirect URI:</li>
              </ol>
              <div className="mt-2 bg-white border border-blue-300 rounded p-2 flex items-center gap-2">
                <code className="text-xs text-blue-900 break-all flex-1 min-w-0">
                  https://xahssxqgkrrlotaunwtg.supabase.co/functions/v1/tiktok-oauth
                </code>
                <CopyButton
                  text="https://xahssxqgkrrlotaunwtg.supabase.co/functions/v1/tiktok-oauth"
                  field="tiktok-redirect"
                />
              </div>
              <p className="text-sm text-blue-800 mt-3 font-medium">
                Required Scopes:
              </p>
              <ul className="text-sm text-blue-800 list-disc list-inside space-y-1 ml-2 mt-2">
                <li>user.info.basic</li>
                <li>video.list</li>
                <li>video.upload</li>
                <li>comment.list</li>
                <li>comment.list.manage</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="pb-6">
          <h3 className="text-lg font-medium mb-4">TikTok Business API</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                App ID (Client ID)
              </label>
              <input
                type="text"
                value={credentials.tiktok_business_client_id}
                onChange={(e) => setCredentials({ ...credentials, tiktok_business_client_id: e.target.value })}
                placeholder="Enter your TikTok Business App ID"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                App Secret
              </label>
              <div className="relative">
                <input
                  type={showSecrets.tiktokBusiness ? 'text' : 'password'}
                  value={credentials.tiktok_business_client_secret}
                  onChange={(e) => setCredentials({ ...credentials, tiktok_business_client_secret: e.target.value })}
                  placeholder="Enter your TikTok Business App Secret"
                  className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets({ ...showSecrets, tiktokBusiness: !showSecrets.tiktokBusiness })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecrets.tiktokBusiness ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800 mb-2 font-medium">
                Setup Instructions:
              </p>
              <ol className="text-sm text-blue-800 list-decimal list-inside space-y-2 ml-2">
                <li>Go to <a href="https://business-api.tiktok.com/portal/apps" target="_blank" rel="noopener noreferrer" className="underline">TikTok Business API Portal</a></li>
                <li>Create a new app or select existing app</li>
                <li>Enable <strong>Mentions</strong> (Content + Comment) and <strong>Ad Comments</strong> products</li>
                <li>Add this redirect URI:</li>
              </ol>
              <div className="mt-2 bg-white border border-blue-300 rounded p-2 flex items-center gap-2">
                <code className="text-xs text-blue-900 break-all flex-1 min-w-0">
                  https://xahssxqgkrrlotaunwtg.supabase.co/functions/v1/tiktok-business-oauth
                </code>
                <CopyButton
                  text="https://xahssxqgkrrlotaunwtg.supabase.co/functions/v1/tiktok-business-oauth"
                  field="tiktok-business-redirect"
                />
              </div>
              <p className="text-sm text-blue-800 mt-3 font-medium">
                Features:
              </p>
              <ul className="text-sm text-blue-800 list-disc list-inside space-y-1 ml-2 mt-2">
                <li>Full comment management (read, write, hide, pin)</li>
                <li>Ad comment moderation</li>
                <li>Business account mentions</li>
                <li>Analytics and metrics</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="pb-6">
          <h3 className="text-lg font-medium mb-4">Pinterest API</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                App ID
              </label>
              <input
                type="text"
                value={credentials.pinterest_app_id}
                onChange={(e) => setCredentials({ ...credentials, pinterest_app_id: e.target.value })}
                placeholder="Enter your Pinterest App ID"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                App Secret
              </label>
              <div className="relative">
                <input
                  type={showSecrets.pinterest ? 'text' : 'password'}
                  value={credentials.pinterest_app_secret}
                  onChange={(e) => setCredentials({ ...credentials, pinterest_app_secret: e.target.value })}
                  placeholder="Enter your Pinterest App Secret"
                  className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets({ ...showSecrets, pinterest: !showSecrets.pinterest })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecrets.pinterest ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                App access level
              </label>
              <select
                value={credentials.pinterest_access_tier}
                onChange={(e) => setCredentials({ ...credentials, pinterest_access_tier: e.target.value === 'standard' ? 'standard' : 'trial' })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="trial">Trial access &mdash; sandbox, pins visible only to you</option>
                <option value="standard">Standard access &mdash; production, pins public</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Pinterest grants every new app Trial access first. Change this only after Pinterest confirms Standard access,
                then reconnect the account &mdash; a sandbox token does not work against the production API.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-900">
                <strong>Trial access does not publish publicly.</strong> Pins and boards created by a Trial app are Sandbox
                entities: the API returns 201, the pin exists in your account, and nobody else can see it &mdash; not in search,
                not in an incognito window. Only Standard access produces public pins. <code>create_pin</code> reports which of
                the two you got in its <code>visibility</code> field.
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800 mb-2 font-medium">
                Setup Instructions:
              </p>
              <ol className="text-sm text-blue-800 list-decimal list-inside space-y-2 ml-2">
                <li>Convert the Pinterest account to a <strong>business account</strong> &mdash; API v5 refuses personal ones</li>
                <li>Go to <a href="https://developers.pinterest.com/apps/" target="_blank" rel="noopener noreferrer" className="underline">Pinterest Developers &rarr; My apps</a> and create an app</li>
                <li>Request Trial access, and Standard access once the app works</li>
                <li>Add this redirect URI:</li>
              </ol>
              <div className="mt-2 bg-white border border-blue-300 rounded p-2 flex items-center gap-2">
                <code className="text-xs text-blue-900 break-all flex-1 min-w-0">
                  https://xahssxqgkrrlotaunwtg.supabase.co/functions/v1/pinterest-oauth
                </code>
                <CopyButton
                  text="https://xahssxqgkrrlotaunwtg.supabase.co/functions/v1/pinterest-oauth"
                  field="pinterest-redirect"
                />
              </div>
              <p className="text-sm text-blue-800 mt-3 font-medium">
                Required Scopes:
              </p>
              <ul className="text-sm text-blue-800 list-disc list-inside space-y-1 ml-2 mt-2">
                <li>user_accounts:read</li>
                <li>boards:read</li>
                <li>boards:write</li>
                <li>pins:read</li>
                <li>pins:write</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-b pb-6">
          <h3 className="text-lg font-medium mb-4">X (Twitter) API</h3>
          <p className="text-sm text-gray-600 mb-4">
            <strong>Important:</strong> X API pricing starts at $200/month for Basic tier (required for reading comments). Free tier only allows posting (1,500 tweets/month).
          </p>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Key (Consumer Key)
              </label>
              <input
                type="text"
                value={credentials.x_api_key}
                onChange={(e) => setCredentials({ ...credentials, x_api_key: e.target.value })}
                placeholder="Enter your X API Key"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                API Secret (Consumer Secret)
              </label>
              <div className="relative">
                <input
                  type={showSecrets.x ? 'text' : 'password'}
                  value={credentials.x_api_secret}
                  onChange={(e) => setCredentials({ ...credentials, x_api_secret: e.target.value })}
                  placeholder="Enter your X API Secret"
                  className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets({ ...showSecrets, x: !showSecrets.x })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecrets.x ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bearer Token (Optional - for app-level operations)
              </label>
              <div className="relative">
                <input
                  type={showSecrets.x ? 'text' : 'password'}
                  value={credentials.x_bearer_token}
                  onChange={(e) => setCredentials({ ...credentials, x_bearer_token: e.target.value })}
                  placeholder="Enter your X Bearer Token"
                  className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets({ ...showSecrets, x: !showSecrets.x })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecrets.x ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800 mb-2 font-medium">
                Pricing Tiers:
              </p>
              <ul className="text-sm text-blue-800 list-disc list-inside space-y-1 ml-2">
                <li><strong>Free:</strong> 1,500 posts/month (posting only, no reading)</li>
                <li><strong>Basic ($200/mo):</strong> 50k posts/month, 10k reads/month</li>
                <li><strong>Pro ($5,000/mo):</strong> 300k posts/month, 1M reads/month</li>
              </ul>
              <p className="text-sm text-blue-800 mt-3 mb-2 font-medium">
                Setup Instructions:
              </p>
              <ol className="text-sm text-blue-800 list-decimal list-inside space-y-1 ml-2">
                <li>Go to <a href="https://developer.twitter.com/en/portal/dashboard" target="_blank" rel="noopener noreferrer" className="underline">X Developer Portal</a></li>
                <li>Create a new app with OAuth 2.0</li>
                <li>Add this redirect URI:</li>
              </ol>
              <div className="mt-2 bg-white border border-blue-300 rounded p-2 flex items-center gap-2">
                <code className="text-xs text-blue-900 break-all flex-1 min-w-0">
                  https://xahssxqgkrrlotaunwtg.supabase.co/functions/v1/x-oauth
                </code>
                <CopyButton
                  text="https://xahssxqgkrrlotaunwtg.supabase.co/functions/v1/x-oauth"
                  field="x-redirect"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="border-b pb-6">
          <div className="flex items-center space-x-2 mb-4">
            <Bell className="w-5 h-5 text-blue-500" />
            <h3 className="text-lg font-medium">Telegram Notifications</h3>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Otrzymuj powiadomienia o odpowiedziach oczekujących na zatwierdzenie bezpośrednio na Telegramie. Możesz zatwierdzać lub odrzucać odpowiedzi jednym kliknięciem.
          </p>
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={credentials.telegram_notifications_enabled}
                  onChange={(e) => setCredentials({ ...credentials, telegram_notifications_enabled: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
              <span className="text-sm font-medium text-gray-700">
                {credentials.telegram_notifications_enabled ? 'Powiadomienia włączone' : 'Powiadomienia wyłączone'}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bot Token
              </label>
              <div className="relative">
                <input
                  type={showSecrets.telegram ? 'text' : 'password'}
                  value={credentials.telegram_bot_token}
                  onChange={(e) => setCredentials({ ...credentials, telegram_bot_token: e.target.value })}
                  placeholder="Wklej token bota od @BotFather"
                  className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowSecrets({ ...showSecrets, telegram: !showSecrets.telegram })}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showSecrets.telegram ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Chat ID
              </label>
              <input
                type="text"
                value={credentials.telegram_chat_id}
                onChange={(e) => setCredentials({ ...credentials, telegram_chat_id: e.target.value })}
                placeholder="Twoje Chat ID (np. 123456789)"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <button
                type="button"
                onClick={async () => {
                  if (!credentials.telegram_bot_token || !credentials.telegram_chat_id) {
                    toast.error('Wprowadź Bot Token i Chat ID');
                    return;
                  }
                  if (!credentials.telegram_notifications_enabled) {
                    toast('Test zostanie wysłany, ale włącz powiadomienia aby działały automatycznie', { icon: '⚠️' });
                  }
                  setTestingTelegram(true);
                  try {
                    const { data: { user } } = await supabase.auth.getUser();
                    if (!user) throw new Error('Not authenticated');

                    await handleSave();

                    const response = await fetch(
                      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/telegram-send-notification`,
                      {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                        },
                        body: JSON.stringify({
                          user_id: user.id,
                          message: '🎉 Testowe powiadomienie z AI Agent HUB!\n\nJeśli widzisz tę wiadomość, konfiguracja Telegram działa poprawnie.',
                          skip_enabled_check: true,
                        }),
                      }
                    );
                    const result = await response.json();
                    if (result.success) {
                      toast.success('Testowe powiadomienie wysłane!');
                    } else {
                      throw new Error(result.error || 'Unknown error');
                    }
                  } catch (error) {
                    console.error('Error sending test notification:', error);
                    toast.error('Nie udało się wysłać powiadomienia');
                  } finally {
                    setTestingTelegram(false);
                  }
                }}
                disabled={testingTelegram || !credentials.telegram_bot_token || !credentials.telegram_chat_id}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                <span>{testingTelegram ? 'Wysyłanie...' : 'Wyślij testowe powiadomienie'}</span>
              </button>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 overflow-hidden">
              <p className="text-sm text-blue-800 mb-2 font-medium">
                Instrukcja konfiguracji:
              </p>
              <ol className="text-sm text-blue-800 list-decimal list-inside space-y-2">
                <li>Otwórz Telegram i znajdź <strong>@BotFather</strong></li>
                <li>Wyślij <code className="bg-white px-1 rounded">/newbot</code> i postępuj zgodnie z instrukcjami</li>
                <li>Skopiuj token bota i wklej powyżej</li>
                <li className="break-words">Aby uzyskać Chat ID:
                  <ul className="list-disc list-inside ml-4 mt-1">
                    <li>Wyślij wiadomość do swojego bota</li>
                    <li className="break-all">Odwiedź: <code className="bg-white px-1 rounded text-xs break-all">api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code></li>
                    <li>Znajdź <code className="bg-white px-1 rounded">"chat": {"{"}id: YOUR_ID{"}"}</code></li>
                  </ul>
                </li>
                <li>Po zapisaniu ustawień, skonfiguruj webhook:
                  <div className="mt-2 bg-white border border-blue-300 rounded p-2 overflow-hidden">
                    <code className="text-xs text-blue-900 break-all block">
                      api.telegram.org/bot&lt;TOKEN&gt;/setWebhook?url=https://xahssxqgkrrlotaunwtg.supabase.co/functions/v1/telegram-webhook
                    </code>
                  </div>
                </li>
              </ol>
              <p className="text-sm text-blue-800 mt-3 font-medium">
                Webhook URL:
              </p>
              <div className="mt-2 bg-white border border-blue-300 rounded p-2 flex items-center gap-2 overflow-hidden">
                <code className="text-xs text-blue-900 break-all flex-1 min-w-0">
                  https://xahssxqgkrrlotaunwtg.supabase.co/functions/v1/telegram-webhook
                </code>
                <CopyButton
                  text="https://xahssxqgkrrlotaunwtg.supabase.co/functions/v1/telegram-webhook"
                  field="telegram-webhook"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end mt-6 pt-6 border-t">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-5 h-5" />
          <span>{saving ? 'Saving...' : 'Save API Credentials'}</span>
        </button>
      </div>
    </div>
  );
}
